/* eslint-disable */
import React, { useState } from 'react';
import {
  Box, Typography, InputBase, Popover,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const FONT = "'Open Sans', sans-serif";

const LABEL_COLOR = '#007BC6';
const VALUE_COLOR = '#1A2B3C';
const PLACEHOLDER_COLOR = '#385576';
const BORDER_DEFAULT = '#DEE5EE';
const BORDER_FOCUS = '#007BC6';
const BG = '#F8FAFC';
const DISABLED_BG = '#F1F4F8';

/* ── Shared wrapper box ─────────────────────────────────────── */
const FieldBox = ({ focused, disabled, children, sx = {}, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      border: `1.5px solid ${focused ? BORDER_FOCUS : BORDER_DEFAULT}`,
      borderRadius: '8px',
      backgroundColor: disabled ? DISABLED_BG : BG,
      px: '12px',
      transition: 'border-color 0.15s',
      minHeight: '52px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      ...sx,
    }}
  >
    {children}
  </Box>
);

const FieldLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '12px',
      fontWeight: 600,
      color: LABEL_COLOR,
      fontFamily: FONT,
      lineHeight: 1,
      mb: '3px',
      userSelect: 'none',
    }}
  >
    {children}
  </Typography>
);

/* ── StyledField ─────────────────────────────────────────────── */
export const StyledField = ({ label, disabled, InputProps, value, onChange, sx, ...props }) => {
  const [focused, setFocused] = useState(false);
  const hasValue = value !== '' && value !== null && value !== undefined;
  const showLabel = hasValue || focused;

  return (
    <FieldBox focused={focused} disabled={disabled} sx={{ pt: showLabel ? '7px' : 0, pb: showLabel ? '6px' : 0, ...sx }}>
      {showLabel && <FieldLabel>{label}</FieldLabel>}
      <InputBase
        value={value}
        onChange={onChange}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={!showLabel ? label : ''}
        sx={{
          fontFamily: FONT,
          fontSize: '14px',
          fontWeight: 600,
          color: VALUE_COLOR,
          p: 0,
          '& .MuiInputBase-input': {
            p: 0,
            fontFamily: FONT,
            fontSize: '14px',
            fontWeight: 600,
            color: VALUE_COLOR,
            '&::placeholder': { color: PLACEHOLDER_COLOR, opacity: 1, fontWeight: 600 },
            '&.Mui-disabled': { WebkitTextFillColor: '#9CA3AF' },
          },
          '& .MuiInputAdornment-root': { ml: 0 },
        }}
        endAdornment={InputProps?.endAdornment}
        {...props}
      />
    </FieldBox>
  );
};

/* ── StyledSelect ────────────────────────────────────────────── */
export const StyledSelect = ({ label, value, onChange, options, name, disabled }) => {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const hasValue = value !== '' && value !== null && value !== undefined;

  // Support both string[] and { value, label }[] options
  const getOptValue = (opt) => (typeof opt === 'string' ? opt : opt.value);
  const getOptLabel = (opt) => (typeof opt === 'string' ? opt : opt.label);
  const displayLabel = hasValue ? (options.find((o) => getOptValue(o) === value) ? getOptLabel(options.find((o) => getOptValue(o) === value)) : value) : label;

  const handleOpen = (e) => { if (!disabled) setAnchor(e.currentTarget); };
  const handleClose = () => setAnchor(null);
  const handleSelect = (opt) => {
    onChange({ target: { name, value: getOptValue(opt) } });
    handleClose();
  };

  return (
    <>
      {/* Trigger */}
      <FieldBox
        focused={open}
        disabled={disabled}
        onClick={handleOpen}
        sx={{
          pt: hasValue ? '7px' : 0,
          pb: hasValue ? '6px' : 0,
          cursor: disabled ? 'default' : 'pointer',
          userSelect: 'none',
        }}
      >
        {hasValue && <FieldLabel>{label}</FieldLabel>}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              flex: 1,
              fontSize: '14px',
              fontWeight: hasValue ? 600 : 500,
              color: hasValue ? VALUE_COLOR : PLACEHOLDER_COLOR,
              fontFamily: FONT,
              lineHeight: 1,
            }}
          >
            {displayLabel}
          </Typography>
          {open
            ? <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#8398AE', flexShrink: 0, transform: 'rotate(180deg)', transition: 'transform 0.15s' }} />
            : <KeyboardArrowDownIcon sx={{ fontSize: 18, color: '#8398AE', flexShrink: 0, transition: 'transform 0.15s' }} />
          }
        </Box>
      </FieldBox>

      {/* Popover options */}
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{ mt: '4px' }}
        PaperProps={{
          sx: {
            width: anchor ? anchor.offsetWidth : 'auto',
            border: '1px solid #E5E9EF',
            borderRadius: '10px',
            boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: '6px' }}>
          {options.map((opt, i) => {
            const optVal = getOptValue(opt);
            const optLbl = getOptLabel(opt);
            return (
              <Box
                key={optVal}
                onClick={() => handleSelect(opt)}
                sx={{
                  px: '10px',
                  py: '10px',
                  cursor: 'pointer',
                  borderBottom: i < options.length - 1 ? '1px solid #F1F5F9' : 'none',
                  '&:hover': { backgroundColor: '#F8FAFC' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: FONT,
                    color: optVal === value ? LABEL_COLOR : VALUE_COLOR,
                    fontWeight: optVal === value ? 600 : 400,
                  }}
                >
                  {optLbl}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};